import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getImageUrlForVariant } from '@/lib/homepage-mappers';
import type { TransformedVideoGalleryData } from '@/types/media-coverage';

interface FeaturedVideoGalleryWrapProps {
  locale: string;
  viewMoreHref: string;
  cmsData?: TransformedVideoGalleryData | null;
}

function YoutubeCtaCard({
  show,
  bg,
  heading,
  buttonText,
  href,
  ariaLabel,
  linkProps,
}: {
  show: boolean;
  bg: string;
  heading: string;
  buttonText: string;
  href: string;
  ariaLabel: string;
  linkProps: ReturnType<typeof getCmsButtonLinkProps>;
}) {
  return (
    <div
      className={`${show ? '' : 'hidden'} relative overflow-hidden`}
      style={{ backgroundColor: '#E31E24' }}
    >
      <div className="flex flex-col md:min-h-[220px] md:flex-row md:items-stretch lg:min-h-[240px]">
        <div className="relative min-h-[190px] w-full shrink-0 px-5 pb-5 pt-2 sm:min-h-full md:order-2 md:min-h-0 md:w-[34%] md:px-0 md:py-8 md:pl-6 md:pr-10 lg:pr-12">
          <CmsImage
            src={bg}
            alt=""
            fill
            className="object-cover [object-position:right_bottom]"
            sizes="(max-width: 768px) 100vw, 34vw"
            aria-hidden
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
        </div>
        <div className="flex flex-1 flex-col justify-center gap-5 px-6 py-7 sm:px-8 sm:py-8 md:order-1 md:px-10 md:py-10 lg:py-12">
          <h2 className="max-w-xl text-lg font-bold leading-tight text-white sm:text-2xl md:text-[1.75rem]">
            {heading}
          </h2>
          <div>
            <SwapTextButton
              tone="light"
              text={buttonText}
              href={href}
              {...linkProps}
              showArrow
              className="shadow-md"
              aria-label={ariaLabel}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactCtaCard({
  show,
  bgColor,
  heading,
  overlay,
  buttonText,
  href,
  ariaLabel,
  linkProps,
}: {
  show: boolean;
  bgColor: string;
  heading: string;
  overlay: string;
  buttonText: string;
  href: string;
  ariaLabel: string;
  linkProps: ReturnType<typeof getCmsButtonLinkProps>;
}) {
  return (
    <div
      className={`${show ? '' : 'hidden'} relative overflow-hidden`}
      style={{ backgroundColor: bgColor }}
    >
      <div className="flex flex-col md:min-h-[220px] md:flex-row md:items-stretch lg:min-h-[240px]">
        <div className="flex flex-1 flex-col justify-center gap-5 px-6 py-7 sm:px-8 sm:py-8 md:px-10 md:py-10 lg:py-12">
          <h2 className="max-w-xl text-lg font-bold leading-tight text-white sm:text-2xl md:text-[1.75rem]">
            {heading}
          </h2>
          <div>
            <SwapTextButton
              tone="light"
              text={buttonText}
              href={href}
              {...linkProps}
              showArrow
              className="shadow-md"
              aria-label={ariaLabel}
            />
          </div>
        </div>
        <div className="relative min-h-[190px] w-full shrink-0 px-5 pb-5 pt-2 sm:min-h-[210px] md:min-h-0 md:w-[34%] md:px-0 md:py-8 md:pl-4 md:pr-10 lg:pr-12">
          <CmsImage
            src={overlay}
            alt=""
            fill
            className="object-contain [object-position:right_bottom]"
            sizes="(max-width: 768px) 100vw, 34vw"
            aria-hidden
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
        </div>
      </div>
    </div>
  );
}

/** Resolve YouTube-CTA card props (CMS values with hardcoded fallbacks). */
function resolveYoutubeCta(
  youtubeCta: NonNullable<TransformedVideoGalleryData['youtubeCta']> | null,
  viewMoreHref: string,
) {
  const button = youtubeCta?.buttonWhite?.[0] ?? youtubeCta?.buttonDark?.[0] ?? null;
  const buttonText = button?.buttonText ?? 'Our YouTube Page';
  return {
    bg: getImageUrlForVariant(youtubeCta?.bgImage ?? null, 'large') ?? '/images/video_gl.jpg',
    heading: youtubeCta?.heading ?? 'Follow Us on YouTube',
    buttonText,
    href: button?.buttonUrl ?? viewMoreHref,
    ariaLabel: button?.ariaLabel || buttonText,
    linkProps: button
      ? getCmsButtonLinkProps(button)
      : { target: '_blank' as const, rel: 'noopener noreferrer' },
  };
}

/** Resolve contact-CTA card props (CMS values with hardcoded fallbacks). */
function resolveContactCta(
  contactCta: NonNullable<TransformedVideoGalleryData['contactCta']> | null,
  contactHref: string,
) {
  const button = contactCta?.buttonWhite?.[0] ?? contactCta?.buttonDark?.[0] ?? null;
  const buttonText = button?.buttonText ?? 'Enquire Now';
  return {
    bgColor: contactCta?.bgColor ?? '#5B0E10',
    heading: contactCta?.heading ?? "We'd Love To Hear From You",
    overlay: getImageUrlForVariant(contactCta?.image ?? null, 'large') ?? '/images/video_gl1.png',
    buttonText,
    href: button?.buttonUrl ?? contactHref,
    ariaLabel: button?.ariaLabel || buttonText,
    linkProps: getCmsButtonLinkProps(button),
  };
}

export default function FeaturedVideoGalleryWrap({
  locale,
  viewMoreHref,
  cmsData,
}: FeaturedVideoGalleryWrapProps) {
  const contactHref = `/${locale}/contact`;
  const youtubeCta = cmsData?.youtubeCta ?? null;
  const contactCta = cmsData?.contactCta ?? null;
  const showYoutubeCta = !cmsData || Boolean(youtubeCta);
  const showContactCta = !cmsData || Boolean(contactCta);

  const youtube = resolveYoutubeCta(youtubeCta, viewMoreHref);
  const contact = resolveContactCta(contactCta, contactHref);

  return (
    <section className="grid grid-cols-1 md:grid-cols-2" aria-label="Featured video gallery wrap">
      <YoutubeCtaCard
        show={showYoutubeCta}
        bg={youtube.bg}
        heading={youtube.heading}
        buttonText={youtube.buttonText}
        href={youtube.href}
        ariaLabel={youtube.ariaLabel}
        linkProps={youtube.linkProps}
      />

      <ContactCtaCard
        show={showContactCta}
        bgColor={contact.bgColor}
        heading={contact.heading}
        overlay={contact.overlay}
        buttonText={contact.buttonText}
        href={contact.href}
        ariaLabel={contact.ariaLabel}
        linkProps={contact.linkProps}
      />
    </section>
  );
}
